import React, { useState, useMemo, useCallback } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";

export default function Places({ setLocationAddress, setCity, setStateValue, setZip, setLatitude, setLongitude, isMobileApp }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyAvEcAoXc_eUTHDbNWKqOZpmS4FsRBCOG8',
    libraries: ["places"],
  });

  if (loadError) return <div className="error">Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;
  
  return (
    <Map 
      setLocationAddress={setLocationAddress} 
      setCity={setCity} 
      setStateValue={setStateValue} 
      setZip={setZip} 
      setLatitude={setLatitude} 
      setLongitude={setLongitude} 
      isMobileApp={isMobileApp}
    />
  );
}

function Map({ setLocationAddress, setCity, setStateValue, setZip, setLatitude, setLongitude, isMobileApp }) {
  const center = useMemo(() => ({ lat: 20.59, lng: 78.96 }), []);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(5);
  const [currentLocationError, setCurrentLocationError] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const getComponentType = useCallback((type, components) => {
    const component = components.find(comp => comp.types.includes(type));
    return component ? component.long_name : null;
  }, []);
  
  const getCurrentLocation = useCallback(async (event) => {
    event.preventDefault();
  
    try {
      if (navigator.geolocation) {
        setIsLoadingLocation(true);
        setCurrentLocationError('Fetching current location...');
        
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
  
              // Fetch address from current location coordinates
              const results = await getGeocode({ location: { lat: latitude, lng: longitude } });
              const { address_components } = results[0];
              const city = getComponentType('locality', address_components);
              const state = getComponentType('administrative_area_level_1', address_components);
              const zip = getComponentType('postal_code', address_components);
  
              setLocationAddress(results[0].formatted_address || '');
              setCity(city);
              setStateValue(state);
              setZip(zip);
              setLatitude(latitude);
              setLongitude(longitude);
              setSelected({ lat: latitude, lng: longitude });
              setZoom(18);
              setCurrentLocationError('');
  
            } catch (error) {
              setCurrentLocationError("Error fetching current location");
            } finally {
              setIsLoadingLocation(false);
            }
          },
          (error) => {
            setCurrentLocationError("Error getting current location");
            setIsLoadingLocation(false);
          },
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 0 }
        );
      } else {
        setCurrentLocationError("Geolocation is not supported by this browser.");
        setIsLoadingLocation(false);
      }
    } catch (error) {
      setCurrentLocationError("Unexpected error occurred");
      setIsLoadingLocation(false);
    }
  }, [getComponentType, setLocationAddress, setCity, setStateValue, setZip, setLatitude, setLongitude]);

  // Map container style to override any conflicting styles
  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginTop: '15px',
    position: 'relative',
    overflow: 'visible',
    display: 'block'
  };

  return (
    <>
      <div className="places-container">
        <div className="controls">
          {!isMobileApp && (
            <button
              className="loc-btn"
              onClick={getCurrentLocation}
              disabled={isLoadingLocation}
            >
              {isLoadingLocation ? 'Getting Location...' : 'Use Current Location'}
            </button>
          )}

          {currentLocationError && (
            <p className="error">
              {currentLocationError}
            </p>
          )}

          <div className="combobox-wrapper">
            <PlacesAutocomplete
              setSelected={setSelected}
              setZoom={setZoom}
              setLocationAddress={setLocationAddress}
              setCity={setCity}
              setStateValue={setStateValue}
              setZip={setZip}
              setLatitude={setLatitude}
              setLongitude={setLongitude}
            />
          </div>
        </div>
      </div>

      <GoogleMap
        zoom={zoom}
        center={selected ?? center}
        mapContainerStyle={mapContainerStyle}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {selected && <Marker position={selected} />}
      </GoogleMap>
    </>
  );
}

const PlacesAutocomplete = ({ setSelected, setZoom, setLocationAddress, setCity, setStateValue, setZip, setLatitude, setLongitude }) => {
  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
  });

  const getComponentType = useCallback((type, components) => {
    const component = components.find(comp => comp.types.includes(type));
    return component ? component.long_name : null;
  }, []);
  
  const handleSelect = useCallback(async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      const { address_components } = results[0];

      setCity(getComponentType('administrative_area_level_2', address_components) ||
              getComponentType('administrative_area_level_3', address_components) ||
              getComponentType('locality', address_components));

      setStateValue(getComponentType('administrative_area_level_1', address_components));
      setZip(getComponentType('postal_code', address_components));

      setSelected({ lat, lng });
      setZoom(18);

      setLocationAddress(address.toString());
      setLatitude(lat);
      setLongitude(lng);
      
      if (typeof window !== 'undefined') {
        window.dataLayer.push({
          event: 'LocationAddress'
        });
      }
    } catch (error) {
      console.error('Error selecting address:', error);
    }
  }, [setValue, clearSuggestions, getGeocode, getLatLng, getComponentType, setCity, setStateValue, setZip, setSelected, setZoom, setLocationAddress, setLatitude, setLongitude]);

  return (
    <Combobox onSelect={handleSelect}>
      <ComboboxInput
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={!ready}
        className="form-control combobox-input"
        placeholder="Kindly provide exact service location"
        style={{
          width: '100%',
          maxWidth: '500px',
          padding: '12px 16px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          fontSize: '14px',
          margin: '0 auto',
          display: 'block'
        }}
      />
      <ComboboxPopover className="results-popover" style={{ background: 'white', cursor: 'pointer' }}>
        <ComboboxList>
          {status === "OK" &&
            data.map(({ place_id, description }) => (
              <ComboboxOption key={place_id} value={description} />
            ))}
        </ComboboxList>
      </ComboboxPopover>
    </Combobox>
  );
};